import React, { useState, useEffect } from 'react';
import { documentsApi, activitiesApi } from '../services/api';
import { FileText, Bot, FileEdit, Pin, Building2, Bell, UploadCloud } from 'lucide-react';
import DiligenceWidget from './DiligenceWidget';
import MissingDocumentsWidget from './MissingDocumentsWidget';
import DiligenceOverviewTable from './DiligenceOverviewTable';
import { loadWidgets } from './SettingsPage';
import './Home.css';

// Activity icon mapping - returns Lucide icon component
const getActivityIcon = (type) => {
  const icons = {
    upload: FileText,
    analysis: Bot,
    summary: FileEdit,
    annotation: Pin,
    deal_created: Building2,
    default: Bell
  };
  return icons[type] || icons.default;
};

function Home({ onNavigate, onViewDiligenceTracker, onSelectDeal }) {
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState(loadWidgets);

  useEffect(() => {
    fetchData();
    const handleWidgetUpdate = () => setWidgets(loadWidgets());
    window.addEventListener('dealnexa:widgets-updated', handleWidgetUpdate);
    return () => window.removeEventListener('dealnexa:widgets-updated', handleWidgetUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // Fetch documents
      const docsResponse = await documentsApi.list().catch(() => ({ data: { documents: [] } }));
      
      let docs = docsResponse.data?.documents || [];
      
      // Sort by completedAt (most recent first), fallback to createdAt
      docs = docs.sort((a, b) => {
        const dateA = new Date(b.completedAt || b.createdAt || 0);
        const dateB = new Date(a.completedAt || a.createdAt || 0);
        return dateA - dateB;
      });
      
      setDocuments(docs.slice(0, 5));

      // Fetch activities
      const activitiesResponse = await activitiesApi.list({ limit: 5 }).catch(() => ({ data: { activities: [] } }));
      setActivities(activitiesResponse.data?.activities || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Set default empty state on error
      setDocuments([]);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <div className="welcome-section">
          <h1>Welcome back!</h1>
          <p>Here's what's happening with your deals today.</p>
        </div>
      </div>

      {/* Diligence Overview Table - Replaces KPI tiles */}
      <DiligenceOverviewTable 
        onViewDiligenceTracker={onViewDiligenceTracker}
        onSelectDeal={onSelectDeal}
      />

      {/* Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Diligence Status Widget */}
        {widgets.find(w => w.id === 'diligence')?.visible !== false && (
          <div className="dashboard-card">
            <DiligenceWidget
              onViewFullTracker={onViewDiligenceTracker}
              onSelectDeal={onSelectDeal}
              onNavigateToDeals={() => onNavigate('deals')}
            />
          </div>
        )}

        {/* Missing Documents Requests Widget */}
        {widgets.find(w => w.id === 'missing')?.visible !== false && (
          <div className="dashboard-card">
            <MissingDocumentsWidget
              onViewDiligenceTracker={onViewDiligenceTracker}
            />
          </div>
        )}

        {/* Recent Documents - Now secondary/right */}
        {widgets.find(w => w.id === 'documents')?.visible !== false && <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h3>Recent Documents</h3>
            <button className="btn-view-all" onClick={() => onNavigate('library')}>
              View All
            </button>
          </div>
          
          {documents.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty-icon"><FileText size={48} /></div>
              <h4>No documents uploaded</h4>
              <p>Upload your first document to get started</p>
            </div>
          ) : (
            <div className="recent-documents-list">
              {documents.map((doc) => (
                <div key={doc.jobId} className="recent-document-item">
                  <span className="recent-doc-icon"><FileText size={18} /></span>
                  <div className="recent-doc-info">
                    <div className="recent-doc-name">{doc.filename}</div>
                    <div className="recent-doc-meta">
                      {doc.completedAt 
                        ? `Analyzed ${new Date(doc.completedAt).toLocaleDateString()}`
                        : doc.createdAt 
                          ? `Uploaded ${new Date(doc.createdAt).toLocaleDateString()}`
                          : 'Recently added'
                      }
                    </div>
                  </div>
                  <span className={`recent-doc-status ${doc.status === 'complete' ? 'analyzed' : 'pending'}`}>
                    {doc.status === 'complete' ? 'Analyzed' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <div className="quick-actions">
            <button className="quick-action-btn" onClick={() => onNavigate('upload')}>
              <span className="quick-action-icon"><UploadCloud size={20} /></span>
              <span className="quick-action-label">Upload Document</span>
            </button>
            <button className="quick-action-btn" onClick={() => onNavigate('deals')}>
              <span className="quick-action-icon"><Building2 size={20} /></span>
              <span className="quick-action-label">Create Deal</span>
            </button>
            <button className="quick-action-btn" onClick={() => onNavigate('annotations')}>
              <span className="quick-action-icon"><Pin size={20} /></span>
              <span className="quick-action-label">Annotate</span>
            </button>
            <button className="quick-action-btn" onClick={() => onNavigate('aifindings')}>
              <span className="quick-action-icon"><Bot size={20} /></span>
              <span className="quick-action-label">AI Analysis</span>
            </button>
          </div>
        </div>}

        {/* Activity Feed */}
        {widgets.find(w => w.id === 'activity')?.visible !== false && <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h3><Bell size={18} /> Recent Activity</h3>
          </div>
          
          {activities.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty-icon"><Bell size={48} /></div>
              <h4>No activity yet</h4>
              <p>Upload documents or create deals to see activity</p>
            </div>
          ) : (
            <div className="activity-list">
              {activities.map((activity) => {
                const ActivityIcon = getActivityIcon(activity.type);
                return (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon"><ActivityIcon size={18} /></div>
                    <div className="activity-content">
                      <div className="activity-text">{activity.title}</div>
                      <div className="activity-time">{activity.time}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

export default Home;
